import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FolderPlus, Upload, Download, Trash2, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { docLibrary } from '@/api/client';
import { PageHeader } from '@/components/layout/PageHeader';
import { EmptyState } from '@/components/ui/EmptyState';
import { DocLibraryFolderTree } from './components/DocLibraryFolderTree';
import { UploadToLibraryDialog } from './components/UploadToLibraryDialog';
import { errorText } from '@/lib/permissionError';

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Document Library — site-wide shared folder/file space (client feedback
 * 2026-08-04: "no role restrictions... shared to entire site"). Every
 * authenticated user sees this page and can create folders, upload, and
 * download — enforced by the backend having no role gate at all on these
 * handlers, not by any client-side check here.
 */
export function DocumentLibraryPage() {
  const qc = useQueryClient();
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);

  const foldersQ = useQuery({
    queryKey: ['doc-library-folders'],
    queryFn: () => docLibrary.listFolders(),
    staleTime: 30_000,
  });
  const folders = foldersQ.data?.folders ?? [];

  const filesQ = useQuery({
    queryKey: ['doc-library-files', selectedFolderId],
    queryFn: () => docLibrary.listFiles(selectedFolderId ?? 'root'),
    staleTime: 15_000,
  });
  const files = filesQ.data?.files ?? [];

  const selectedFolder = folders.find((f) => f.folder_id === selectedFolderId) ?? null;
  const folderLabel = selectedFolder?.name ?? 'Home';

  const createFolderMut = useMutation({
    mutationFn: (name: string) =>
      docLibrary.createFolder({ name, parent_folder_id: selectedFolderId ?? undefined }),
    onSuccess: () => {
      toast.success('Folder created');
      qc.invalidateQueries({ queryKey: ['doc-library-folders'] });
      setNewFolderName('');
      setCreatingFolder(false);
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to create folder'));
    },
  });

  const deleteFolderMut = useMutation({
    mutationFn: (folderId: string) => docLibrary.deleteFolder(folderId),
    onSuccess: () => {
      toast.success('Folder deleted');
      qc.invalidateQueries({ queryKey: ['doc-library-folders'] });
      setSelectedFolderId(null);
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to delete folder'));
    },
  });

  const deleteFileMut = useMutation({
    mutationFn: (fileId: string) => docLibrary.deleteFile(fileId),
    onSuccess: () => {
      toast.success('File deleted');
      qc.invalidateQueries({ queryKey: ['doc-library-files'] });
    },
    onError: (err) => {
      toast.error(errorText(err, 'Failed to delete file'));
    },
  });

  async function handleDownload(fileId: string) {
    try {
      const { download_url } = await docLibrary.getDownloadUrl(fileId);
      window.open(download_url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(errorText(err, 'Failed to open file'));
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Document Library"
        subtitle="Shared folders and files for everyone at this site — no role restrictions."
        actions={
          <div className="flex items-center gap-2">
            <button type="button" className="btn-secondary text-xs" onClick={() => setCreatingFolder(true)}>
              <FolderPlus className="h-3.5 w-3.5" /> New folder
            </button>
            <button type="button" className="btn-primary text-xs" onClick={() => setUploading(true)}>
              <Upload className="h-3.5 w-3.5" /> Upload
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[240px_1fr]">
        <div className="card p-3">
          {foldersQ.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-ink-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading folders…
            </div>
          ) : (
            <DocLibraryFolderTree folders={folders} selectedFolderId={selectedFolderId} onSelect={setSelectedFolderId} />
          )}
        </div>

        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-ink-primary">{folderLabel}</h3>
            {selectedFolderId && (
              <button
                type="button"
                className="text-xs text-danger-700 hover:underline"
                onClick={() => {
                  if (confirm(`Delete the "${folderLabel}" folder? It must be empty.`)) {
                    deleteFolderMut.mutate(selectedFolderId);
                  }
                }}
                disabled={deleteFolderMut.isPending}
              >
                Delete this folder
              </button>
            )}
          </div>

          {creatingFolder && (
            <div className="flex items-center gap-2 rounded border border-surface-border bg-surface-muted/40 p-2">
              <input
                autoFocus
                className="input flex-1 text-xs"
                placeholder="Folder name"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newFolderName.trim()) createFolderMut.mutate(newFolderName.trim());
                  if (e.key === 'Escape') { setCreatingFolder(false); setNewFolderName(''); }
                }}
              />
              <button
                type="button"
                className="btn-primary text-xs"
                disabled={!newFolderName.trim() || createFolderMut.isPending}
                onClick={() => createFolderMut.mutate(newFolderName.trim())}
              >
                Create
              </button>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={() => { setCreatingFolder(false); setNewFolderName(''); }}
              >
                Cancel
              </button>
            </div>
          )}

          {filesQ.isLoading ? (
            <div className="flex items-center gap-2 text-xs text-ink-muted">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading files…
            </div>
          ) : files.length === 0 ? (
            <EmptyState
              title="No files here"
              description="Upload a file or pick a different folder."
              icon={<FileText className="h-6 w-6" />}
            />
          ) : (
            <table className="data-table w-full text-sm">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {files.map((f) => (
                  <tr key={f.file_id}>
                    <td className="flex items-center gap-1.5">
                      <FileText className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
                      <span className="truncate">{f.file_name}</span>
                    </td>
                    <td className="text-ink-muted">{fmtBytes(f.file_size)}</td>
                    <td className="text-ink-muted">{f.uploaded_at.slice(0, 10)}</td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        type="button"
                        className="text-ink-muted hover:text-ink-primary"
                        onClick={() => handleDownload(f.file_id)}
                        aria-label="Download"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        className="ml-2 text-ink-muted hover:text-danger-700"
                        onClick={() => {
                          if (confirm(`Delete "${f.file_name}"?`)) deleteFileMut.mutate(f.file_id);
                        }}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <UploadToLibraryDialog
        open={uploading}
        onClose={() => setUploading(false)}
        folderId={selectedFolderId ?? undefined}
        folderLabel={folderLabel}
        onUploaded={() => qc.invalidateQueries({ queryKey: ['doc-library-files', selectedFolderId] })}
      />
    </div>
  );
}
